/**
 * Common misspellings of Noticeboard's own words. The matching model reads a
 * misspelled word as something else entirely, so "whats in my calender" misses
 * the Calendar.
 */
const MISSPELLINGS: Record<string, string> = {
  calender: "calendar",
  templats: "templates",
  sheduled: "scheduled",
  remnd: "remind",
  wich: "which",
  memebers: "members",
  hv: "have",
  u: "you",
  uplaod: "upload",
  upolad: "upload",
  uplod: "upload",
  foldr: "folder",
  foler: "folder",
  floder: "folder",
  calandar: "calendar",
  calander: "calendar",
  calendr: "calendar",
  notcie: "notice",
  noticie: "notice",
  noitce: "notice",
  notic: "notice",
  notce: "notice",
  ntoice: "notice",
  cirlce: "circle",
  cirle: "circle",
  certifcate: "certificate",
  certficate: "certificate",
  certificat: "certificate",
  libary: "library",
  liberary: "library",
  shedule: "schedule",
  schedual: "schedule",
  shcedule: "schedule",
  signiture: "signature",
  signture: "signature",
  templete: "template",
  tempate: "template",
  attachement: "attachment",
  gaurdian: "guardian",
  parnet: "parent",
  evnet: "event",
  evnt: "event",
  whn: "when",
  ppl: "people",
  wen: "when",
  wat: "what",
  wats: "whats",
  wots: "whats",
  nxt: "next",
  tmrw: "tomorrow",
  tomorow: "tomorrow",
  tmr: "tomorrow",
  tmoz: "tomorrow",
  "2moro": "tomorrow",
  "2day": "today",
  whr: "where",
  hw: "how",
  chagne: "change",
  craete: "create",
  creat: "create",
  crete: "create",
  evryone: "everyone",
  everone: "everyone",
  eveyone: "everyone",
  urgnet: "urgent",
  urgnt: "urgent",
  swich: "switch",
  wher: "where",
  remindr: "reminder",
  reminer: "reminder",
};

/**
 * Everyday words for Noticeboard's own: a school says "class groups" and "docs",
 * the catalogue says Circles and files, and the model doesn't always join them up.
 */
const EVERYDAY_WORDS: [RegExp, string][] = [
  [/\bclass(?:es)? groups\b/gi, "circles"],
  [/\bclass group\b/gi, "circle"],
  [/\b(?:docs|documents)\b/gi, "files"],
  // "which posts are scheduled?", "which guardians have not joined?": Noticeboard's own words
  [/\bposts\b/gi, "notices"],
  [/\b(?:guardians|families)\b(?=\s+(?:have|haven'?t|has|hasn'?t|did|didn'?t|are|aren'?t)\b)/gi, "parents"],
  [/^look up\b/i, "Find"],
  [/\b(?:doc|document)\b(?= in drive\b)/gi, "file"],
];

const MISSPELLED = new RegExp(`\\b(${Object.keys(MISSPELLINGS).join("|")})(s?)\\b`, "gi");

/** Words that open a message without saying anything: "okay, what's on this week?" */
const OPENING = /^(?:(?:ok(?:ay)?|okey|so|um+|uh+|right|alright|well|hmm+)\b[\s,.!-]*)+/i;

/**
 * A bridge into the next question: "and claim vs poll", "wait, our package".
 * Left in place when only one word follows ("and tomorrow"), so a date is not thrown away.
 */
const LEAD = /^(?:(?:and|also|sorry)\b[\s,.!-]*|wait\b(?!\s*,?\s*go back)[\s,.!-]*)+/i;

/** "Like I said, where is it?" — the reminder is not the question. */
const ASIDE =
  /^(?:(?:like i said|just tell me|ok but|wait,?\s+go back|um,?\s+the thing is|i(?:'m| am) not very good with computers)\b[\s,.!:-]*)+/i;

/** "Can you show me how to attach it?" is the same question as "How do I attach it?" */
const SHOW_ME = /^(?:can you show me how to|(?:please\s+)?show me how to|what(?:'s| is) the way to|how to)\b/i;
const TRYING = /^i(?:'m| am) trying to\b(.+?)\s+and i can(?:'|no)?t find it\b[.?!]*$/i;

/**
 * A greeting, a thank-you, or praise in front of the real question:
 * "hi, where are my notices?", "thanks, how do I create an event?".
 * Left in place when nothing follows, so "hi" and "thanks" stay small talk.
 */
const SOCIAL =
  /^(?:(?:hi+|hey+|hiya|hello+|howdy|good (?:morning|afternoon|evening|day))(?:\s+(?:there|noticeboard|nb|again))?\b[\s,.!-]*|(?:thanks?(?: you)?|thank you|ty|thx|cheers|ta)(?:\s+now)?\s*[,!]+\s*)/i;

/**
 * "that's great, help" — the comma marks it as a reaction, not the start of a sentence.
 * "great and the launch of umhlanga" has no comma; "and" still drops the reaction.
 * "Great news about the gala" stays, because the next word is part of the sentence.
 */
const PRAISE_WORDS =
  "amazing|awesome|brilliant|wonderful|fantastic|excellent|impressive|lovely|perfect|great|nice|helpful|incredible|cool";

/** "that's not a problem, what is a team member?" — the dismissal isn't the question. */
const DISMISSAL = /^(?:that'?s|thats)\s+not\s+a\s+problem\s*[,.]?\s*/i;
const PRAISE_THEN_QUESTION = "when|what|whats|where|why|which|how|who|can|could|do|does|is|are|will";
const PRAISE = new RegExp(
  `^(?:(?:oh|ah|wow|really|that'?s|thats)\\s+)*(?:${PRAISE_WORDS})\\b(?:\\s*,\\s*(?:and\\s+)?|\\s+and\\s+|\\s+(?=(?:${PRAISE_THEN_QUESTION}|take me|show me|open)\\b))`,
  "i"
);

/** "I just need to know how do I create an event?" — they restate the purpose, then the question. */
const PURPOSE =
  /^(?:(?:i just need to know|i need to know|i need to find out|i want to know|i(?:'d|d) like to know|i just need to|i just need|what i(?:'m| am) trying to do is|all i want is to|all i want is|basically)\b[\s,.!-]*)+/i;

/** "Not WhatsApp. How do parents get our messages?" — they name the wrong thing, then the real question. */
const NOT_THAT = /^not (?:whatsapp|the register|zoom|email|fees)[.!]\s+/i;

/** "I'm looking at the screen. Where are my notices?" */
const ON_SCREEN = /^i(?:'m| am) looking at the screen[.!]\s+/i;

/** "A parent just asked me, where is the notice?" */
const RELAY =
  /^(?:a parent just asked me|the principal wants to know|another teacher asked)[:,]?\s+/i;

/**
 * A polite or hurried opening, longer phrases first so "please" does not eat
 * "could you please tell me".
 */
const FRAME =
  /^(?:could you please tell me|could you please explain|would you mind telling me|i was wondering|can you please|before the bell|in one sentence|quick one|please)\b[:,]?\s+/i;

/** "1) How do I create an event?" — a line from a note. */
const NUMBERED = /^\d{1,2}[).:-]\s+/;

/** "Switch test: how do I send a notice?" — a label in front of the question. */
const LABEL = /^(?:switch test|test)\s*:\s*/i;

/**
 * How people actually start a question: "sorry quick question. where is add user?"
 * Dropped only when the question is still there, so "hey" on its own stays a greeting.
 */
const CHAT_LEAD =
  /^(?:(?:sorry quick question|quick question|a parent just asked|im new here|i'?m new here|plain version please|our office lady wants to know|before the bell|for the grade 2 teacher|dumb question|on my phone|the principal asked|just so i dont mess it up|real quick|after assembly|checking i understood|hey)\b[\s,.!:;-]*)+/i;

/**
 * Framing that says how they're asking, never what: "Just checking:", "I know the dashboard.",
 * "Can you help?". Dropped only when a request still follows, so "Can you help?" alone is still that.
 */
const FRAMING_LEAD = new RegExp(
  "^(?:" +
    [
      "(?:ok(?:ay)? )?(?:thanks|thank you)[.!]+\\s*",
      "anyway\\s*[,.:!]\\s*",
      "will do[.,!]+\\s*(?:anyway\\s+)?",
      "noted,? thank you[.,!]+",
      "thanks,? i'?ve told the principal[.,!]+",
      "(?:ok|okay),? i'?ll do that[.,!]+",
      "(?:can|could) you (?:quickly |please )?help(?: me)?(?: (?:out|with this|with something|here))?\\s*[?:.!,]",
      "(?:hi|hello|hey)[, ]+quick one\\s*[-:,.!]",
      "i'?m new\\s*[,.:!]",
      "random question but\\s+",
      "good (?:afternoon|evening)\\s*[.!,-]",
      "(?:can|could) (?:you|u) check\\s*:\\s*",
      "help me out(?: here)?\\s*:",
      "please help me (?:locate|find) this\\s*:",
      "can we do this\\s*\\?",
      "i (?:use noticeboard (?:daily|every day)|know the dashboard|have used this before|am in a hurry|'m in a hurry)\\s*[.!,]",
      "(?:in|on) (?:noticeboard|the dashboard)\\s*[,:]",
      "i need this in noticeboard\\s*:",
      "for our school\\s*[:,]",
      "(?:just checking|my next (?:small )?task|one thing before class|before i send an update)\\s*[:,]",
      "(?:back again|hello again|morning|good morning)\\s*[!.,—–-]+",
      "quick q\\s*[:.,!]",
      "sorry if this is obvious but\\s*[:,]?",
      "sorry,? (?:a )?quick question\\s*[.:,!]",
      "sorry to (?:bother|trouble)(?: you)?\\s*[,.!:]",
      "(?:the principal|my principal|the head|my hod|a parent|a colleague|another teacher) (?:is asking|asked|wants to know)\\s*[:,-]",
      "i'?ve got a parent on the phone\\s*[-,:.]",
      "(?:before|after) (?:assembly|class|break|lunch)\\s*[-,:]",
    ].join("|") +
    ")\\s*",
  "i"
);

/**
 * A closing word on how to answer, never part of the request: "… I just need the right screen.",
 * "… No need for a long explanation.", "… Where would I click?". The request before it is the question.
 */
const FRAMING_TAIL = new RegExp(
  "[\\s,.;:!?—–-]*\\b(?:" +
    [
      "i just need the right screen",
      "no need for a long explanation",
      "only show me where to go",
      "(?:can|could) you take me there",
      "i need the (?:correct|right) next step",
      "please keep (?:the answer|it) short",
      "where would i click",
      "i am on create at the moment",
      "i do not need anything published",
      "this is for the class i manage",
      "i mean the noticeboard feature",
      "can we check that first",
      "please point me to the right place",
      "thanks",
      "thank you",
      "cheers|thx|pls|ta",
      "please",
    ].join("|") +
    ")[\\s.!?]*$",
  "i"
);

/**
 * What they say not to do, said before the request: "Do not send anything. Find my notices.",
 * "Please do not start a poll; I want to see the votes". The request is what's left.
 */
const NOT_THIS_FIRST =
  /^(?:(?:please|and)\s+)?(?:do not|don'?t|i do not want to|i don'?t want to|no need to)\s+(?:send|start|create|make|change|write|publish|add|issue|set up|prepare|remind)\b[^,.;:?!]*(?:[.;:!]\s+|,\s+(?=(?:but\s+)?(?:i\s+)?(?:just\s+)?(?:want|need|show|find|what|where|how|can)\b))/i;

/**
 * A contrast after the request: "Show the calendar, do not create an event", "Where are worksheets,
 * not my uploaded files?", "Can I check who read it without sending a reminder?". The request is before it.
 */
const NOT_THIS_AFTER =
  /(?:\s*[,;—–-]\s*(?:but\s+)?(?:(?:please\s+)?(?:do not|don'?t|dont)\s+|not\s+(?=(?:to\s+)?(?:add|create|make|send|start|write|issue|publish|set\s+up|remind|change|edit|delete|prepare|my|our|a\s+new|the\s+new|any)\b)|no\s+new\s+)|\s+(?:without|rather than|instead of)\s+)[^,;?.!]*([?.!]*)$|([?.!])\s+(?:please\s+)?(?:do not|don'?t)\s+[^.?!]*[.?!]?$/i;

/** "Find my draft, do not send it" as "Find my draft": the request, without what they said not to do. */
export function withoutWhatNotToDo(text: string): string {
  const first = text.replace(NOT_THIS_FIRST, "").trim();
  const said = first.split(/\s+/).length >= 2 ? first : text;
  const after = said.replace(NOT_THIS_AFTER, (_, end1?: string, end2?: string) => end1 || end2 || "").trim();
  return after.split(/\s+/).length >= 2 ? after : said;
}

/** "Right, …", "Morning! …", "… Thanks.": the framing taken off both ends while a request is still left. */
export function withoutFraming(text: string): string {
  text = text.replace(/\s*[:;]-?[)D]\s*$/g, "").replace(/\s+asap[?.!]*$/i, "");
  let said = text.trim();
  for (let i = 0; i < 3; i += 1) {
    const lead = said.replace(FRAMING_LEAD, "").trim();
    // "where are signatures? please" keeps its question mark
    const tail = /\b(?:message|body|text|saying|should say)\b/i.test(lead) ? lead : lead.replace(FRAMING_TAIL, (closing: string) => (/^\s*\?/.test(closing) ? "?" : "")).trim();
    // "Ok but thanks" is still thanks: what's left can't end on a word that leads into the rest
    // "Can you help? Cancel": a one-word command is still a whole request
    const leftWhole = (part: string) =>
      /^(?:cancel|stop|undo|skip|poll|notice|event)[.!]*$/i.test(part) || (part.split(/\s+/).length >= 2 && !/\b(?:but|and|or|ok|okay|so)$/i.test(part));
    const next = leftWhole(tail) ? tail : leftWhole(lead) ? lead : said;
    if (next === said) break;
    // "Find my notices No need for a long explanation" ends without a stop once the tail is gone
    said = next;
  }
  return said;
}

/**
 * The message as the matcher should read it: misspelled Noticeboard words fixed,
 * and an opening "okay" or "so" dropped. A message that is nothing but those stays as it is.
 */
export function tidyMessage(text: string): string {
  const fixed = text.replace(/\bwhere\s+r\b/gi,"where are").replace(/\bwitha\.?/gi, "with a").replace(/\bwho\s+red\b/gi, "who read").replace(MISSPELLED, (_, word: string, plural: string) => {
    const right = MISSPELLINGS[word.toLowerCase()];
    return (word[0] === word[0].toUpperCase() ? right[0].toUpperCase() + right.slice(1) : right) + plural;
  });
  const everyday = withoutWhatNotToDo(withoutFraming(EVERYDAY_WORDS.reduce((text, [pattern, word]) => text.replace(pattern, word), fixed)));
  const corrected = takeCorrection(everyday);
  const asideFirst = dropIfQuestionFollows(corrected, ASIDE);
  const opened = asideFirst.replace(OPENING, "").trim();
  const led = opened.replace(LEAD, "").trim();
  const body = led.split(/\s+/).filter(Boolean);
  const chosen =
    body.length >= 2 ||
    /^(?:thanks?|thank you|hi+|hey+|hello+|perfect|great|good|nice|awesome|brilliant|wonderful|fantastic|excellent|lovely|cool|bye|goodbye|goodnight|cheers|ta)[?.!]*$/i.test(led)
      ? led
      : opened;
  const kept = chosen || corrected || fixed;
  const social = withoutFraming(dropIfQuestionFollows(kept, SOCIAL));
  const praised = dropIfQuestionFollows(social, PRAISE);
  const dismissed = dropIfQuestionFollows(praised, DISMISSAL);
  const purposed = dropIfQuestionFollows(dismissed, PURPOSE);
  const aside = [NOT_THAT, ON_SCREEN, RELAY, FRAME, NUMBERED, LABEL, CHAT_LEAD, CHAT_LEAD].reduce(
    (text, pattern) => dropIfQuestionFollows(text, pattern),
    purposed
  );
  const asideAgain = dropIfQuestionFollows(aside, ASIDE);
  // "remind me how polls work" asks how polls work; it's not a reminder
  const shown = asideAgain
    .replace(/^(?:can you\s+|could you\s+)?remind me\s+(?=(?:how|what|where|when|why|which|who)\b)/i, "")
    .replace(/^how\s+(?!(?:it|this|that|do|does|can|i|you|we)\b)(\w+(?:\s+\w+)?)\s+works?\b/i, "How do $1 work")
    .replace(SHOW_ME, "How do I")
    // "take payments for the staff briefing" asks about payments, whatever they're for
    .replace(/\b(take|collect) (payments?|money|fees) (?:for|at)\b[^?.!]*/i, "$1 $2")
    .replace(/\bsend(?: the)?\b.+\bonly to that circle\b/i, "send it only to that circle")
    .replace(/^(where do i put a file) for\b.+/i, "$1?");
  const trying = shown.match(TRYING);
  // "Quick one: I do not want to create a poll. …": what not to do, once the opening is gone too
  const asked = withoutWhatNotToDo(trying ? `How do I${trying[1]}` : shown);
  // "I need to send an urgent notice about the cake sale" asks for one, with its topic: kept whole
  if (/^i need to send an urgent notice about\b/i.test(asked)) return asked;
  return asked.replace(
    /^(how do i|how can i|where do i|i need to|help me) (create a circle|invite a parent|create a certificate|send an urgent notice|find a template|make a claim list|send a notice to one parent|add a staff member|make a circle|tell members) (?:for|about)\b.+/i,
    "$1 $2"
  );
}

/** "active adverts, sorry, how do I create an event?" — the real question is after the correction. */
function takeCorrection(text: string): string {
  const parts = text.split(/,\s*sorry,\s+/i);
  if (parts.length < 2) return text;
  const tail = parts[parts.length - 1].trim();
  return tail ? tail : text;
}

/** Drops a social opening only when a real question is still left. "Hi?" is still just hello. "Thanks, that's enough" is still thanks. */
function dropIfQuestionFollows(text: string, pattern: RegExp): string {
  const next = text.replace(pattern, "").trim();
  if (!next || next === text || !/[a-z0-9]/i.test(next)) return text;
  if (/^(that'?s|thats)\b/i.test(next) && /^(?:thanks|thank you|hi|hello|hey)\b/i.test(text)) return text;
  if (/^i(?:'ll| will) (?:ask|publish)\b/i.test(next) && /^(?:thanks|thank you)\b/i.test(text)) return text;
  return next;
}
